import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ColorPicker,
  CustomInput,
  FormButton,
  MessageBox,
  NoResultsSection,
} from "reusableComponents";
import { createPlaceGroupService } from "services/placeGroupService";
import styles from "./styles.module.scss";
import { useSelector } from "react-redux";
import GroupCard from "./GroupCard";
import { useModal } from "context";
import GroupFormModal from "modals/GroupFormModal";
import LoadingPage from "views/LoadingPage";

export const CalendarGroupsView = () => {
  const { t } = useTranslation("global");
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState({ label: "", color: "" });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const token = useSelector((state) => state.auth.token);
  const PlaceGroupService = createPlaceGroupService(token);
  const [error, setError] = useState(null);
  const { openModal, closeModal } = useModal();

  const pickerOptions = {
    required: true,
    format: 'hex',
    swatchOnly: true,
    colors: [
      // Reds/Pinks (5)
      "#FF5252", "#FF1744", "#F50057", "#FF4081", "#C51162",
      
      // Purples/Violets (5)
      "#E040FB", "#D500F9", "#6200EA", "#7C4DFF", "#4527A0",
      
      // Blues (6)
      "#2962FF", "#2979FF", "#0091EA", "#00B0FF", "#00E5FF", "#006064",
      
      // Teals/Greens (6)
      "#1DE9B6", "#00BFA5", "#00C853", "#64DD17", "#AEEA00", "#33691E",
      
      // Yellows/Oranges (5)
      "#FFD600", "#FFAB00", "#FF6D00", "#FF3D00", "#DD2C00",
      
      // Special/Neutrals (1)
      "#795548"
    ]
  };

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setIsLoading(true);
        const response = await PlaceGroupService.getGroups();
        setGroups(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGroups();
  }, []);

  const refreshGroups = async () => {
    try {
      const response = await PlaceGroupService.getGroups();
      setGroups(response.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmit = async (groupData) => {
    try {
      setIsLoading(true);
      let response;
      if (groupData.id) {
        response = await PlaceGroupService.updateGroup(groupData);
      } else {
        response = await PlaceGroupService.createGroup(groupData);
      }
      await refreshGroups();
      return response.data;
    } catch (err) {
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const addGroup = () => {
    openModal({
      title: t("calendar.groups.addGroup"),
      content: (
        <GroupFormModal
          group={{ label: "", color: "" }}
          onSubmit={handleSubmit}
          pickerOptions={pickerOptions}
          confirmText={t("buttons.save")}
        />
      ),
      onClose: closeModal,
    });
  };

  const editGroup = (group) => {
    openModal({
      title: t("calendar.groups.edit"),
      content: (
        <GroupFormModal
          group={group}
          onSubmit={handleSubmit}
          pickerOptions={pickerOptions}
          confirmText={t("buttons.save")}
        />
      ),
      onClose: closeModal,
    });
  };

  const deleteGroup = async (group) => {
    try {
      setIsLoading(true);
      const response = await PlaceGroupService.deleteGroup(group);
      await refreshGroups(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEdit = () => {
    setNewGroup(null);
    setFormSubmitted(false);
  };

  const handleModalResult = React.useCallback(
    (result) => {
      // Only proceed if we have valid places data
      if (!result?.data?.places) {
        closeModal();
        return;
      }

      setGroups((prevGroups) => {
        // Find the index of the group we need to update
        const groupIndex = prevGroups.findIndex((g) => g.id === result.data.id);
        if (groupIndex === -1) return prevGroups; // Group not found

        // Check if places actually changed
        const currentPlaces = prevGroups[groupIndex].places;
        const newPlaces = result.data.places;

        if (
          currentPlaces.length === newPlaces.length &&
          currentPlaces.every((p, i) => p.id === newPlaces[i]?.id)
        ) {
          return prevGroups; // No changes needed
        }

        // Create new array with updated group
        return [
          ...prevGroups.slice(0, groupIndex),
          { ...prevGroups[groupIndex], places: newPlaces },
          ...prevGroups.slice(groupIndex + 1),
        ];
      });

      closeModal();
    },
    [selectedGroup?.id, closeModal]
  );

  const removeFromGroup = async (space, group) => {
    const spacesIds = group.places
      .filter((place) => space.id !== place.id)
      .map((place) => place.id);

    try {
      setIsLoading(true);
      const response = await PlaceGroupService.updateGroupLinks(group.id, {
        places: spacesIds,
      });
      const updatedGroups = groups.map((g) => {
        if (g.id === group.id) {
          return { ...g, places: response.data.places };
        }
        return g;
      });
      setGroups(updatedGroups);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && groups.length === 0) {
    return <LoadingPage />;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <section className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.calendarPageTitle}>
          {t("calendar.groups.title")}
        </h2>
        <div className={styles.addGroupBtnContainer}>
          <FormButton
            label={`+ ${t("calendar.groups.addGroup")}`}
            onClick={addGroup}
            variant="primary"
            disabled={isLoading}
          />
        </div>
      </header>
      <MessageBox message={t("calendar.groups.text")} />

      {/* {newGroup && (
        <div className={`${styles.card} ${styles.groupForm}`}>
          <form onSubmit={submitForm}>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <CustomInput
                  label={t('calendar.groups.name')}
                  labelAbove
                  name="title"
                  value={newGroup.label || ''}
                  handleChange={(e) => setNewGroup({ ...newGroup, label: e.target.value })}
                  placeholder={t('calendar.groups.namePlaceholder')}
                  required
                  error={formSubmitted && !newGroup.label ? t('validation.required') : null}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.colorLabel}>{t('calendar.groups.color')}</label>
                <ColorPicker
                  value={newGroup.color}
                  onChange={(color) => setNewGroup({ ...newGroup, color })}
                  options={pickerOptions}
                />
                {formSubmitted && !newGroup.color && (
                  <span className={styles.error}>{t('validation.required')}</span>
                )}
              </div>
            </div>
            <div className={styles.formActions}>
              <FormButton 
                label={t('buttons.cancel')}
                onClick={cancelEdit}
                variant="gray"
                disabled={isLoading}
                size = "medium"
              />
              <FormButton 
                label={newGroup.id ? t('calendar.groups.edit') : t('buttons.validate')}
                type="submit"
                variant="primary"
                disabled={isLoading}                
                size = "medium"

              />
            </div>
          </form>
        </div>
      )} */}

      <div className={styles.groupsList}>
        {groups.map((group, index) => (
          <GroupCard
            key={group.id}
            group={group}
            index={index}
            t={t}
            editGroup={editGroup}
            deleteGroup={deleteGroup}
            removeFromGroup={removeFromGroup}
            openModal={openModal}
            isLoading={isLoading}
            onGroupsUpdate={handleModalResult}
            placeGroupService={PlaceGroupService}
          />
        ))}
      </div>

      {groups.length === 0 && !newGroup && (
        <NoResultsSection
          title={t("calendar.groups.noGroupsTitle")}
          text={t("calendar.groups.noGroupsText")}
        />
      )}
    </section>
  );
};

export default CalendarGroupsView;
